import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import { useTranslation } from 'react-i18next'
import { Info, Lightbulb, OctagonAlert, TriangleAlert } from 'lucide-react'
import type { NodeViewProps } from '@tiptap/react'
import type { LucideIcon } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

/**
 * Callout block (design 7a). The schema is shared with mobile's editor
 * webview and the API's markdown converter (GFM alerts), so the node name,
 * the `variant` values and the HTML shape must stay in step with both.
 */
export const CALLOUT_VARIANTS = ['info', 'tip', 'warning', 'danger'] as const
export type CalloutVariant = (typeof CALLOUT_VARIANTS)[number]

const isVariant = (value: unknown): value is CalloutVariant =>
  CALLOUT_VARIANTS.includes(value as CalloutVariant)

const VARIANT_ICON: Record<CalloutVariant, LucideIcon> = {
  info: Info,
  tip: Lightbulb,
  warning: TriangleAlert,
  danger: OctagonAlert,
}

declare module '@tiptap/core' {
  // Declaration merging needs Tiptap's own type-parameter name.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Commands<ReturnType> {
    callout: {
      /** Wrap the current block(s) in a callout. */
      setCallout: (attrs?: { variant?: CalloutVariant }) => ReturnType
      /** Wrap in a callout, or lift the selection out of one. */
      toggleCallout: (attrs?: { variant?: CalloutVariant }) => ReturnType
    }
  }
}

function CalloutView({ node, updateAttributes, editor }: NodeViewProps) {
  const { t } = useTranslation('common')
  const variant: CalloutVariant = isVariant(node.attrs.variant) ? node.attrs.variant : 'info'
  const Icon = VARIANT_ICON[variant]

  return (
    <NodeViewWrapper data-type="callout" data-variant={variant} className="moly-callout">
      {editor.isEditable ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            contentEditable={false}
            aria-label={t('editor.callout.change')}
            title={t('editor.callout.change')}
            className="moly-callout__icon rounded-sm transition-opacity hover:opacity-70"
          >
            <Icon className="size-[17px]" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {CALLOUT_VARIANTS.map((value) => {
              const ItemIcon = VARIANT_ICON[value]
              return (
                <DropdownMenuItem
                  key={value}
                  onSelect={() => updateAttributes({ variant: value })}
                  className={cn(value === variant && 'font-medium')}
                >
                  <ItemIcon className="size-4" data-variant={value} />
                  {t(`editor.callout.${value}`)}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span contentEditable={false} className="moly-callout__icon">
          <Icon className="size-[17px]" />
        </span>
      )}
      <NodeViewContent className="moly-callout__body" />
    </NodeViewWrapper>
  )
}

export const CalloutExtension = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: 'info',
        parseHTML: (element) => {
          const value = element.getAttribute('data-variant')
          return isVariant(value) ? value : 'info'
        },
        renderHTML: (attributes) => ({ 'data-variant': attributes.variant as string }),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="callout"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'callout' }), 0]
  },

  addNodeView() {
    return ReactNodeViewRenderer(CalloutView)
  },

  addCommands() {
    return {
      setCallout:
        (attrs) =>
        ({ commands }) =>
          commands.wrapIn(this.name, attrs),
      toggleCallout:
        (attrs) =>
        ({ commands }) =>
          commands.toggleWrap(this.name, attrs),
    }
  },
})
